import { GridColDef } from "@mui/x-data-grid";
import { enumToStringConverter, formatDateAndTime, moneyFormat } from "../../../helpers/appHelpers";
import { Avatar } from "@mui/material";
import { PayrollRunStatus } from "../../../models/payroll.dto";

export const CompanyPayrollRunColumns: GridColDef[] = [
	{
		field: "payPeriod",
		headerName: "Pay Period",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value?.code,
	},
	{
		field: "initiatedBy",
		headerName: "Initiated By",
		headerClassName: "super-app-theme--header",
		minWidth: 260,
		flex: 1,
		renderCell: (params) => {
			const user = params.value || undefined;
			return (
				<div className="flex items-center space-x-4">
					{user && <>
						<Avatar
							src={user.photoUrl || ""}
							alt="user"
							className="w-10 h-10 rounded-full"
						/>
						<p className="text-sm">
							{/* {user.firstName + " " + user.lastName} */}
							Admin
						</p>
					</>}
				</div>
			);
		},
	},
	{
		field: "totalCost",
		headerName: "Total Cost",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "numberOfEmployees",
		headerName: "Number Of Employees",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => new Intl.NumberFormat().format(Number(params.value)) || "--",
	},
	{
		field: "processedEmployees",
		headerName: "Processed Employees",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => new Intl.NumberFormat().format(Number(params.value)) || "--",
	},
	{
		field: "status",
		headerName: "Status",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		renderCell(params) {
			return (<>
				{params.value ? (
					<div
						className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
						style={{ color: getPayrollRunStatusColor(params.value) }}
					>
						<div
							className="absolute inset-0"
							style={{
								background: getPayrollRunStatusColor(params.value),
								opacity: "15%"
							}}
						/>
						<p>{params.value === "ERROR" ? "Failed" : enumToStringConverter(params.value)}</p>
					</div>
				):(
					<p>--</p>
				)}
			</>);
		},
	},
	{
		field: "createdAt",
		headerName: "Date Created",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		sortComparator: (date1, date2) => new Date(date1).getTime() - new Date(date2).getTime(),
		valueGetter: (params) => formatDateAndTime(params),
	},
	{
		field: "actions",
		headerName: "Actions",
		minWidth: 150,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
];

export const getPayrollRunStatusColor = (status: PayrollRunStatus) => {
	switch(status) {
		case "COMPLETED":
			return "#4CAF50"
		case "ERROR":
			return "#F44336"
		case "PROCESSING":
			return "#FFC107"
		case "SUBMITTED":
			return "#2196F3"
		default:
			return "#BF21F3"
	}
};

export const CompanyPayrollRunColumnVisibilityModel: Record<string, boolean> = {
	payPeriod: true,
	initiatedBy: true,
	totalCost: true,
	numberOfEmployees: true,
	processedEmployees: false,
	status: true,
	createdAt: true,
};

export const CompanyPayslipsColumns: GridColDef[] = [
	{
		field: "employee",
		headerName: "Employee",
		headerClassName: "super-app-theme--header",
		minWidth: 260,
		flex: 1,
		renderCell: (params: any) => {
			const employee = params.value;
			return (
				<div className="flex items-center space-x-4">
					{employee && <>
						<Avatar
							src={employee?.photoUrl || ""}
							alt="employee"
							className="w-10 h-10 rounded-full"
						/>
						<p className="text-sm truncate">
							{employee?.firstName + " " + employee?.lastName}
						</p>
					</>}
				</div>
			);
		},
	},
	{
		field: "basicPayAmount",
		headerName: "Basic Pay",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "grossAmount",
		headerName: "Gross Pay",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "netPayAmount",
		headerName: "Net Pay",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "employeePensionAmount",
		headerName: "Employee Pension Contribution",
		minWidth: 250,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "employerPensionAmount",
		headerName: "Employer Pension Contribution",
		minWidth: 250,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "taxAmount",
		headerName: "Income Tax",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "employeeSavingSchemeAmount",
		headerName: "Employee Saving Scheme Amount",
		minWidth: 280,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "employerSavingSchemeAmount",
		headerName: "Employer Saving Scheme Amount",
		minWidth: 280,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "allowanceAmount",
		headerName: "Allowance Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "overtimeAmount",
		headerName: "Overtime Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "bonusAmount",
		headerName: "Bonus Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "backpayAmount",
		headerName: "Backpay Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "loanRepaymentAmount",
		headerName: "Loan Repayment Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "partialSalaryAmount",
		headerName: "Partial Salary Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "salaryAdvanceAmount",
		headerName: "Salary Advance Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "unionDeductionsAmount",
		headerName: "Union Deductions Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "actions",
		headerName: "Actions",
		minWidth: 150,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
];

export const CompanyPayslipsColumnVisibilityModel: Record<string, boolean> = {
	employee: true,
	grossAmount: true,
	basicPayAmount: true,
	netPayAmount: true,
	employeePensionAmount: true,
	employerPensionAmount: true,
	taxAmount: true,
	employeeSavingSchemeAmount: false,
	employerSavingSchemeAmount: false,
	allowanceAmount: false,
	overtimeAmount: false,
	bonusAmount: false,
	backpayAmount: false,
	loanRepaymentAmount: false,
	partialSalaryAmount: false,
	salaryAdvanceAmount: false,
	unionDeductionsAmount: false,
};

export const SalaryReportColumns: GridColDef[] = [
	{
		field: "employeeCode",
		headerName: "Employee Code",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "employeeName",
		headerName: "Employee Name",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "basicPayAmount",
		headerName: "Basic Pay",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "allowanceAmount",
		headerName: "Allowance Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "grossAmount",
		headerName: "Gross Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "ssfEmployeeAmount",
		headerName: "SSF Employee Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "taxableAmount",
		headerName: "Taxable Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "incomeTaxAmount",
		headerName: "Income Tax Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "savingSchemesAmount",
		headerName: "Saving Schemes Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "otherDeductionsAmount",
		headerName: "Other Deductions Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "loansAmount",
		headerName: "Loan Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "totalDeductionsAmount",
		headerName: "Total Deductions Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "bonusAmount",
		headerName: "Bonus Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "primaryPayAmount",
		headerName: "Primary Pay",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "secondaryPayAmount",
		headerName: "Secondary Pay",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "deductibleReliefAmount",
		headerName: "Deductible Reliefs",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "totalReliefAmount",
		headerName: "Total Reliefs",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "explicitDeductionAmount",
		headerName: "Explicit Deductions",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "netAmount",
		headerName: "Net Pay",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
];


export const SSFReportColumns: GridColDef[] = [
	{
		field: "id",
		headerName: "Employee Code",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "lastName",
		headerName: "Last Name",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "firstName",
		headerName: "First Name",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "otherNames",
		headerName: "Other Names",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
    {
        field: "fullName",
        headerName: "Full Name",
        minWidth: 220,
        flex: 1,
        headerClassName: "super-app-theme--header",
    },
    {
        field: "email",
        headerName: "Email Address",
        minWidth: 220,
        flex: 1,
        headerClassName: "super-app-theme--header",
    },
    {
        field: "msisdn",
        headerName: "Contact Number",
        minWidth: 220,
        flex: 1,
        headerClassName: "super-app-theme--header",
    },
	{
		field: "ssn",
		headerName: "SSN",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "basicSalary",
		headerName: "Basic Salary",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "employeeContribution",
		headerName: "Employee Contribution",
		minWidth: 250,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "employerContribution",
		headerName: "Employer Contribution",
		minWidth: 250,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "total",
		headerName: "Contribution Total",
		minWidth: 250,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "tierAmount",
		headerName: "Tier Amount",
		minWidth: 210,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "nationalIdNumber",
		headerName: "National ID Number",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},

];

export const SavingSchemeColumns: GridColDef[] = [
	{
		field: "id",
		headerName: "Employee Code",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "lastName",
		headerName: "Surname",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "firstName",
		headerName: "First Name",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "birthDate",
		headerName: "Date of Birth",
		minWidth: 240,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "employmentDate",
		headerName: "Date Joined Company",
		minWidth: 240,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "ssn",
		headerName: "Social Security Number",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "basicSalary",
		headerName: "Basic Salary",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "employeeContribution",
		headerName: "Employee Contribution",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "employerContribution",
		headerName: "Employer Contribution",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "msisdn",
		headerName: "Phone Number",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
    {
        field: "gender",
        headerName: "Sex",
        minWidth: 220,
        flex: 1,
        headerClassName: "super-app-theme--header",
    }
];

export const BankReportColumns: GridColDef[] = [
	{
		field: "EMPLOYEE_CODE",
		headerName: "Employee Code",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "EMPLOYEE_NAME",
		headerName: "Employee Name",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "BANK_CODE",
		headerName: "Bank Code",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value || "--",
	},
	{
		field: "BANK_NAME",
		headerName: "Bank Name",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value || "--",
	},
	{
		field: "NET",
		headerName: "Net Amount",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "ACCOUNT_NUMBER",
		headerName: "Account Number",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value || "--",
	},
	{
		field: "BANK_BRANCH_CODE",
		headerName: "Branch Code",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value || "--",
	},
	{
		field: "BANK_BRANCH_NAME",
		headerName: "Branch Name",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value || "--",
	},
	{
		field: "PERIOD DATE",
		headerName: "Period Dates",
		minWidth: 250,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => {
			const value = params.row?.["PERIOD DATE"];
			if (!value) return "--";

			const date = new Date(value as string);
			if (isNaN(date.getTime())) return "--";

			return date.toLocaleDateString("en-GB", {
				day: "2-digit",
				month: "2-digit",
				year: "2-digit",
			});
		},
	},
	{
		field: "REMARKS",
		headerName: "Remark",
		minWidth: 250,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value || "--",
	},
];

export const TaxReportColumns: GridColDef[] = [
	{
		field: "employeeTaxNumber",
		headerName: "TIN/NATIONAL ID",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value || "--",
	},
	{
		field: "employeeName",
		headerName: "Employee Name",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "position",
		headerName: "Position",
		minWidth: 180,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "residencyType",
		headerName: "Residency Type",
		minWidth: 180,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "basicSalaryAmount",
		headerName: "Basic Salary",
		minWidth: 180,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "isSecondaryEmployment",
		headerName: "Secondary Employment",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => (params.value ? "Yes" : "No"),
	},
	{
		field: "paidSSNIT",
		headerName: "Paid SSNIT",
		minWidth: 160,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => (params.value ? "Yes" : "No"),
	},
	{
		field: "socialSecurityAmount",
		headerName: "SSNIT Amount",
		minWidth: 180,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "thirdTier",
		headerName: "3rd Tier",
		minWidth: 140,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "allowanceAmount",
		headerName: "Allowance",
		minWidth: 160,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "bonusAmount",
		headerName: "Bonus",
		minWidth: 160,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "bonusTaxAmount",
		headerName: "Bonus Tax",
		minWidth: 160,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "excessBonusAmount",
		headerName: "Excess Bonus",
		minWidth: 160,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "totalCashEmolument",
		headerName: "Cash Emolument",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "benefitInKindAmount",
		headerName: "Benefit In Kind",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "totalAssessableIncomeAmount",
		headerName: "Assessable Income",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "deductibleReliefAmount",
		headerName: "Deductible Relief",
		minWidth: 160,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "totalReliefAmount",
		headerName: "Total Relief",
		minWidth: 160,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "chargeableIncomeAmount",
		headerName: "Chargeable Income",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "taxDeductibleAmount",
		headerName: "Tax Deductible",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "overtimeIncomeAmount",
		headerName: "Overtime Income",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "overtimeTaxAmount",
		headerName: "Overtime Tax",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
	{
		field: "totalTaxPayableAmount",
		headerName: "Total Tax Payable",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => moneyFormat(params.value),
	},
];

export const PayrollSummaryColumns: GridColDef[] = [
	{
		field: "particulars",
		headerName: "Particulars",
		minWidth: 250,
		flex: 2,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "debit",
		headerName: "Debit",
		minWidth: 150,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value !== null ? moneyFormat(params.value) : "--",
		align: "right",
		headerAlign: "right",
	},
	{
		field: "credit",
		headerName: "Credit",
		minWidth: 150,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value !== null ? moneyFormat(params.value) : "--",
		align: "right",
		headerAlign: "right",
	},
];

export const SalaryAdvanceReportColumns: GridColDef[] = [
	{
		field: "employeeCode",
		headerName: "Employee Code",
		minWidth: 150,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.row.isStationHeader ? '' : params.value,
	},
	{
		field: "employeeName",
		headerName: "Employee Name",
		minWidth: 200,
		flex: 2,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.row.isStationHeader ? params.row.stationName : params.value,
	},
	{
		field: "advanceAmount",
		headerName: "Advance Amount",
		minWidth: 150,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) =>
			params.row.isStationHeader
				? '' // Leave blank for header rows
				: params.value !== null
					? moneyFormat(params.value)
					: "--",
		align: "right",
		headerAlign: "right",
	},
];



